<script setup lang="ts">
import { ChevronDownIcon } from '@heroicons/vue/24/outline'

const props = defineProps<{
  id: string
  label: string
  expanded: boolean
  reorderable?: boolean
  hasActiveChild?: boolean
  dropPlace?: 'before' | 'after' | null
  dragging?: boolean
}>()

const emit = defineEmits<{
  toggle: []
  dragstart: [event: DragEvent]
  dragover: [event: DragEvent]
  drop: [event: DragEvent]
  dragend: [event: DragEvent]
}>()

let suppressClick = false

function onHeaderDragStart(event: DragEvent) {
  if (!props.reorderable) {
    event.preventDefault()
    return
  }
  suppressClick = true
  event.dataTransfer?.setData('text/plain', props.id)
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
  emit('dragstart', event)
}

function onHeaderClick() {
  if (suppressClick) return
  emit('toggle')
}

function onHeaderKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    emit('toggle')
  }
}

function onHeaderDragEnd(event: DragEvent) {
  emit('dragend', event)
  window.setTimeout(() => {
    suppressClick = false
  }, 0)
}

function onDragOver(event: DragEvent) {
  event.preventDefault()
  emit('dragover', event)
}

function onDrop(event: DragEvent) {
  event.preventDefault()
  emit('drop', event)
}
</script>

<template>
  <div
    class="relative pt-3 first:pt-1"
    :class="{ 'opacity-50': dragging }"
    @dragover="onDragOver"
    @drop="onDrop"
  >
    <div
      v-if="dropPlace === 'before'"
      class="absolute inset-x-2 -top-0.5 h-0.5 rounded-full bg-forest"
    />

    <div
      role="button"
      tabindex="0"
      class="flex w-full select-none items-center gap-1 rounded-md px-1.5 py-1"
      :class="[
        reorderable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        hasActiveChild && !expanded ? 'bg-forest-50' : 'hover:bg-gray-50',
      ]"
      :draggable="!!reorderable"
      :aria-expanded="expanded"
      :aria-controls="`sidebar-section-${id}`"
      :aria-label="`${expanded ? 'Collapse' : 'Expand'} ${label}`"
      :title="reorderable ? 'Drag to reorder' : undefined"
      @click="onHeaderClick"
      @keydown="onHeaderKeydown"
      @dragstart="onHeaderDragStart"
      @dragend="onHeaderDragEnd"
    >
      <span
        class="min-w-0 flex-1 text-[10px] font-semibold tracking-wider uppercase"
        :class="hasActiveChild ? 'text-forest-700' : 'text-gray-400'"
      >
        {{ label }}
      </span>
      <ChevronDownIcon
        class="h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150"
        :class="expanded ? '' : '-rotate-90'"
        aria-hidden="true"
      />
    </div>

    <div
      :id="`sidebar-section-${id}`"
      class="grid transition-[grid-template-rows] duration-200 ease-out"
      :class="expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
    >
      <div class="overflow-hidden">
        <div class="space-y-0.5" role="group" :aria-label="label">
          <slot />
        </div>
      </div>
    </div>

    <div
      v-if="dropPlace === 'after'"
      class="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full bg-forest"
    />
  </div>
</template>
